// ─── ONBOARDING QUIZ CONTENT ───────────────────────────────────────────────────
// All copy/options for the onboarding quiz (Section 1 + Section 2) live here,
// separate from the flow components, so wording can be tweaked without
// touching layout/interaction logic.
import { DEFAULT_CATEGORIES } from "../constants/icons";

export const OBSTACLE_OPTIONS = [
  { id: "distraction", label: "Getting distracted easily / lack of focus" },
  { id: "procrastination", label: "Procrastination / leaving things till the last minute" },
  { id: "doomscrolling", label: "Too much time on social media / doomscrolling" },
  { id: "losing-track", label: "Losing track of tasks and deadlines" },
  { id: "no-routine", label: "Not having a consistent routine" },
  { id: "burnout", label: "Burnout / running low on energy" },
  { id: "overthinking", label: "Overthinking instead of taking action" },
  { id: "juggling", label: "Too many things to juggle at once" },
  { id: "no-start", label: "Not knowing where to start" },
  { id: "sticking-long-term", label: "Trouble sticking with habits long-term" },
];

export const OBSTACLE_NONE_STRUGGLING = { id: "none", label: "None of these, it's something else" };

// The 8 categories the "starter Spaces" step shows, curated from the app's 14
// DEFAULT_CATEGORIES — order matches the planning sketch (left column, then
// right column): Self-Care, Fitness, Chores, Productivity | Finance, Work,
// Creative & Projects. Learning was removed (Jade, 2026-10-11). School rides
// along at the end — picking
// it triggers the "what level are you in?" follow-up the same way Work
// triggers "what industry?" (see OnboardingSection3.jsx / OnboardingFlow.jsx).
const STARTER_CATEGORY_IDS = [
  "cat-hygiene", "cat-fitness", "cat-school", "cat-productivity",
  "cat-finance", "cat-work", "cat-art", "cat-home", // School and Chores swapped (Jade, 2026-10-11)
];
export const STARTER_CATEGORIES = STARTER_CATEGORY_IDS
  .map(id => DEFAULT_CATEGORIES.find(c => c.id === id))
  .filter(Boolean);

// Triggered follow-up questions for the "Work" and "School" starter
// categories — asked right after the categories screen, not as a separate
// section (see OnboardingSection3.jsx / OnboardingFlow.jsx). Comprehensive
// course/job lists — every one ends in "Other" so nothing is a dead end.
// Courses and job titles are picked via a search+A-Z panel
// (OnboardingSearchPicker); fields/industries/levels are small enough to
// stay plain option lists.
export const EDUCATION_LEVELS = [
  { id: "elementary", label: "Elementary" },
  { id: "high-school", label: "High School" },
  { id: "college", label: "College" },
];

// field -> course list (comprehensive version). "Other" appended in code
// (see OTHER_OPTION) rather than repeated in every array.
export const COLLEGE_FIELDS = [
  { id: "business", label: "Business & Management", courses: [
    "Business Administration", "Accountancy", "Accounting Information System", "Management Accounting",
    "Marketing Management", "Human Resource Management", "Financial Management", "Entrepreneurship",
    "Economics", "Business Economics", "Office Administration", "Hospitality Management",
    "Tourism Management", "Real Estate Management", "Customs Administration", "Legal Management",
    "International Business", "Supply Chain / Logistics Management", "Operations Management",
    "Public Administration",
  ] },
  { id: "tech", label: "Computer Science & Information Technology", courses: [
    "Computer Science", "Information Technology", "Information Systems", "Computer Engineering",
    "Software Engineering", "Data Science", "Cybersecurity", "Game Development / Game Design",
    "Multimedia Arts", "Library and Information Science", "Network Technology", "Web Development",
    "Animation", "Digital Arts",
  ] },
  { id: "engineering", label: "Engineering", courses: [
    "Civil Engineering", "Electrical Engineering", "Electronics Engineering", "Mechanical Engineering",
    "Industrial Engineering", "Chemical Engineering", "Computer Engineering", "Aerospace Engineering",
    "Mining Engineering", "Metallurgical Engineering", "Geodetic Engineering", "Sanitary Engineering",
    "Mechatronics Engineering", "Petroleum Engineering", "Agricultural and Biosystems Engineering",
    "Environmental Engineering", "Materials Engineering",
  ] },
  { id: "health", label: "Health & Medicine", courses: [
    "Nursing", "Medicine", "Pharmacy", "Medical Technology / Medical Laboratory Science",
    "Physical Therapy", "Occupational Therapy", "Radiologic Technology", "Respiratory Therapy",
    "Dentistry", "Optometry", "Veterinary Medicine", "Nutrition and Dietetics", "Midwifery",
    "Public Health", "Speech-Language Pathology", "Psychology (Clinical track)",
  ] },
  { id: "sciences", label: "Sciences (Natural & Physical)", courses: [
    "Biology", "Chemistry", "Physics", "Mathematics", "Statistics", "Environmental Science",
    "Marine Biology", "Microbiology", "Molecular Biology and Biotechnology", "Geology",
    "Meteorology", "Food Science and Technology", "Forensic Science",
  ] },
  { id: "humanities", label: "Arts & Humanities", courses: [
    "English Literature / Literature", "Philosophy", "History", "Creative Writing", "Fine Arts",
    "Theater Arts", "Linguistics", "Religious Studies", "Filipino / Panitikan", "Cultural Studies",
  ] },
  { id: "social-sciences", label: "Social Sciences", courses: [
    "Psychology", "Sociology", "Political Science", "Anthropology", "Social Work",
    "International Studies", "Development Studies", "Geography", "Behavioral Science",
  ] },
  { id: "education", label: "Education", courses: [
    "Elementary Education", "Secondary Education", "Early Childhood Education", "Special Education",
    "Physical Education", "Technology and Livelihood Education", "Values Education",
    "Educational Management",
  ] },
  { id: "law", label: "Law & Political Science", courses: [
    "Political Science", "Legal Management", "Law (Juris Doctor)", "Criminology",
    "Public Administration", "International Relations",
  ] },
  { id: "architecture", label: "Architecture & Design", courses: [
    "Architecture", "Interior Design", "Landscape Architecture", "Urban and Regional Planning",
    "Industrial Design", "Fashion Design", "Graphic Design",
  ] },
  { id: "agriculture", label: "Agriculture & Environmental Science", courses: [
    "Agriculture", "Agribusiness", "Forestry", "Fisheries", "Agricultural Engineering",
    "Environmental Science", "Veterinary Medicine", "Horticulture",
  ] },
  { id: "music", label: "Music & Performing Arts", courses: [
    "Music", "Music Education", "Theater Arts", "Dance", "Musical Theater", "Film / Cinema",
    "Broadcasting",
  ] },
  { id: "communication", label: "Communication & Media", courses: [
    "Communication Arts", "Journalism", "Broadcasting", "Advertising", "Public Relations",
    "Film / Cinema", "Digital Media",
  ] },
  { id: "hospitality", label: "Hospitality & Tourism", courses: [
    "Hotel and Restaurant Management", "Tourism Management", "Culinary Arts",
    "Cruise Line Operations", "Airline Operations / Aviation",
  ] },
  { id: "maritime", label: "Maritime Studies", courses: [
    "Marine Transportation (Deck)", "Marine Engineering",
  ] },
  { id: "criminology", label: "Criminology & Criminal Justice", courses: [
    "Criminology", "Forensic Science", "Industrial Security Management",
  ] },
];

// industry -> job title list (comprehensive version).
export const WORK_INDUSTRIES = [
  { id: "biz-finance", label: "Business, Finance & Admin", jobs: [
    "Accountant", "Auditor", "Financial Analyst", "Bookkeeper", "Bank Teller", "Loan Officer",
    "Investment Analyst", "Business Analyst", "Administrative Assistant", "Executive Assistant",
    "Office Manager", "HR Generalist", "HR Recruiter", "Payroll Specialist", "Operations Manager",
    "Project Manager", "Management Consultant", "Compliance Officer", "Insurance Agent",
    "Real Estate Agent / Broker",
  ] },
  { id: "tech", label: "Technology & IT", jobs: [
    "Software Engineer / Developer", "Web Developer", "Mobile App Developer", "QA / Test Engineer",
    "DevOps Engineer", "Data Analyst", "Data Scientist", "Data Engineer", "IT Support Specialist",
    "Systems Administrator", "Network Engineer", "Cybersecurity Analyst", "Database Administrator",
    "UX/UI Designer", "Product Manager (Tech)", "Cloud Engineer", "AI/ML Engineer", "Game Developer",
  ] },
  { id: "engineering", label: "Engineering", jobs: [
    "Civil Engineer", "Mechanical Engineer", "Electrical Engineer", "Electronics Engineer",
    "Chemical Engineer", "Industrial Engineer", "Structural Engineer", "Environmental Engineer",
    "Project Engineer", "Quality Engineer", "Manufacturing Engineer", "Mining Engineer",
  ] },
  { id: "healthcare", label: "Healthcare & Medicine", jobs: [
    "Nurse", "Physician / Doctor", "Pharmacist", "Medical Technologist", "Physical Therapist",
    "Occupational Therapist", "Radiologic Technologist", "Dentist", "Caregiver", "Midwife",
    "Nutritionist-Dietitian", "Psychologist", "Psychiatrist", "Medical Assistant",
    "Healthcare Administrator",
  ] },
  { id: "education", label: "Education & Training", jobs: [
    "Teacher (Elementary)", "Teacher (Secondary)", "Professor / College Instructor", "Tutor",
    "School Administrator", "Guidance Counselor", "Corporate Trainer", "Curriculum Developer",
    "Special Education Teacher",
  ] },
  { id: "sales-marketing", label: "Sales & Marketing", jobs: [
    "Sales Representative", "Sales Manager", "Account Executive", "Marketing Specialist",
    "Digital Marketing Specialist", "SEO Specialist", "Social Media Manager", "Brand Manager",
    "Market Researcher", "Business Development Officer", "Retail Sales Associate",
  ] },
  { id: "creative", label: "Creative, Arts & Design", jobs: [
    "Graphic Designer", "Illustrator", "Animator", "Photographer", "Videographer / Video Editor",
    "Interior Designer", "Fashion Designer", "Product Designer", "Art Director", "Musician",
    "Writer / Author",
  ] },
  { id: "media", label: "Media, Communications & Journalism", jobs: [
    "Journalist / Reporter", "Content Writer", "Copywriter", "Editor", "Broadcaster / News Anchor",
    "Public Relations Officer", "Communications Specialist", "Social Media Content Creator",
    "Podcaster / Streamer",
  ] },
  { id: "retail", label: "Retail & Customer Service", jobs: [
    "Customer Service Representative", "Call Center Agent", "Store Manager", "Cashier",
    "Merchandiser", "Barista", "Sales Clerk",
  ] },
  { id: "hospitality", label: "Hospitality, Travel & Food Service", jobs: [
    "Hotel Front Desk Officer", "Flight Attendant", "Chef / Cook", "Restaurant Manager",
    "Waiter / Waitress", "Tour Guide", "Travel Agent", "Event Planner", "Housekeeping Staff",
  ] },
  { id: "trades", label: "Construction & Skilled Trades", jobs: [
    "Construction Worker", "Electrician", "Plumber", "Carpenter", "Welder",
    "Mechanic / Automotive Technician", "HVAC Technician", "Foreman / Site Supervisor",
  ] },
  { id: "government", label: "Government & Public Service", jobs: [
    "Government Employee (LGU / National)", "Police Officer", "Firefighter", "Military Personnel",
    "Social Worker (Government)", "Barangay Official",
  ] },
  { id: "legal", label: "Legal Services", jobs: [
    "Lawyer / Attorney", "Paralegal", "Legal Assistant", "Court Stenographer", "Notary Public",
  ] },
  { id: "science", label: "Science & Research", jobs: [
    "Research Scientist", "Lab Technician", "Research Assistant", "Environmental Scientist",
    "Data Researcher",
  ] },
  { id: "transport", label: "Transportation & Logistics", jobs: [
    "Delivery Rider / Driver", "Truck Driver", "Logistics Coordinator", "Supply Chain Analyst",
    "Warehouse Staff", "Seafarer / Merchant Marine", "Pilot", "Dispatcher",
  ] },
  { id: "agriculture", label: "Agriculture & Environment", jobs: [
    "Farmer", "Agricultural Technician", "Fisherfolk", "Veterinarian", "Forester",
    "Agribusiness Officer",
  ] },
  { id: "bpo", label: "BPO / Call Center", jobs: [
    "Customer Support Representative", "Technical Support Representative", "Virtual Assistant",
    "Team Leader (BPO)", "Quality Analyst (BPO)", "Chat / Email Support Specialist",
  ] },
  { id: "freelance", label: "Freelance / Self-Employed / Entrepreneur", jobs: [
    "Freelance Writer", "Freelance Designer", "Freelance Developer", "Online Seller / E-commerce Owner",
    "Small Business Owner", "Consultant (Freelance)", "Content Creator / Influencer",
  ] },
];

// Field-specific starter habits (per exact course/job, not per field/industry
// template) live in onboardingFieldHabits.js — COLLEGE_COURSE_HABITS,
// WORK_JOB_HABITS, and the OTHER_FIELD_HABITS fallback.

// Appended to the end of every course/job search panel — reveals a free-text
// field instead of auto-advancing, since picking it means none of the listed
// options fit.
export const OTHER_OPTION = "Other";

// Default frequency each starter suggestion is added with, so a habit card
// never has to sit at "Not set" just because the user hasn't opened it yet
// — genuinely-daily actions (brushing teeth, drinking water) get `daily`,
// everything else gets a specific-days cadence that reads as realistic for
// that kind of task (chores, workouts, weekly reviews). All frequency types
// (daily, specific-days, count, monthly, yearly) are free-tier — this is
// just a starting point, still fully editable from the habit's
// edit form like any other field. Day indices match renderHabitForm's
// Mon..Sun order (0 = Mon).
const FREQ = {
  daily: { frequencyType: "daily" },
  weekdays: { frequencyType: "specific", days: [0, 1, 2, 3, 4] },
  monWedFri: { frequencyType: "specific", days: [0, 2, 4] },
  tueThuSat: { frequencyType: "specific", days: [1, 3, 5] },
  monThu: { frequencyType: "specific", days: [0, 3] },
  tueFri: { frequencyType: "specific", days: [1, 4] },
  wedSat: { frequencyType: "specific", days: [2, 5] },
  wed: { frequencyType: "specific", days: [2] },
  fri: { frequencyType: "specific", days: [4] },
  sat: { frequencyType: "specific", days: [5] },
  sun: { frequencyType: "specific", days: [6] },
};

// Default time-of-day slot for each starter suggestion (paired with `freq`
// above) — the habit card's "Not set" reminder checks both frequency AND
// time of day, so a suggestion needs both to start off fully customized.
// Single-slot, matching a starter habit's default "once a day" count.
const TOD = {
  morning: ["morning"],
  afternoon: ["afternoon"],
  evening: ["evening"],
};

// School and Work's suggestions (onboardingFieldHabits.js's ~3,240
// per-course/job entries, plus the generic cat-school/cat-work fallback
// lists) are too many to hand-tag one by one like the categories above —
// but the copy itself already carries real cadence signal, so this reads
// it instead of defaulting everything to a flat "daily":
//  - "today"/"tomorrow" phrasing ("Review today's lecture notes", "Plan
//    tomorrow's top priority") is a genuinely daily action.
//  - "week"/"weekly" phrasing ("Review my week every Sunday") is a single
//    weekly touchpoint, not a daily one.
//  - Career-development actions (following up, networking, updating a
//    resume/portfolio) don't need daily repetition either.
//  - Everything else still isn't literally daily — studying/working is a
//    workday thing for most people — so it falls back to weekdays only
//    rather than all 7 days.
export function inferFieldFrequency(name) {
  const n = name.toLowerCase();
  if (/\btoday|tomorrow'?s?\b/.test(n)) return { freq: FREQ.daily, tod: TOD.evening };
  if (/\bweek(ly)?\b/.test(n)) return { freq: FREQ.fri, tod: TOD.evening };
  if (/\bfollow up|network(ing)?|resume|portfolio|linkedin|job (search|application)|\bapply\b/.test(n)) {
    return { freq: FREQ.monThu, tod: TOD.afternoon };
  }
  return { freq: FREQ.weekdays, tod: TOD.evening };
}

// ─── STARTER HABIT SUGGESTIONS ────────────────────────────────────────────────
// 10 per category, each tagged with the OBSTACLE_OPTIONS id(s) it best
// addresses. The starter-habits screen (OnboardingSection2.jsx) sorts each
// category's list by how many tags overlap with the user's picked obstacles
// (highest first; ties keep this file's order — a stable sort), so the menu
// feels tailored without ever hiding an option. Users who picked "None of
// these" (or skipped Section 1 entirely) see the list in the order below,
// unsorted. Each suggestion also carries a `freq` (see FREQ above) so it's
// added with a sensible starting frequency instead of "Not set".
export const STARTER_HABITS = {
  "cat-hygiene": [
    { name: "Brush my teeth", emoji: "🦷", tags: ["sticking-long-term", "no-routine"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Wash my face", emoji: "🧴", tags: ["no-routine", "burnout"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Take a shower", emoji: "🚿", tags: ["burnout", "no-routine"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Moisturize before bed", emoji: "🌙", tags: ["no-routine", "sticking-long-term"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Do a 10-minute skincare reset", emoji: "✨", tags: ["burnout", "overthinking"], freq: FREQ.monThu, tod: TOD.evening },
    { name: "Take my vitamins / meds", emoji: "💊", tags: ["losing-track", "sticking-long-term"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Do a face mask", emoji: "🧖", tags: ["burnout", "overthinking"], freq: FREQ.wedSat, tod: TOD.evening },
    { name: "Trim my nails", emoji: "💅", tags: ["no-routine", "procrastination"], freq: FREQ.sun, tod: TOD.evening },
    { name: "Change into comfy clothes after work", emoji: "👕", tags: ["burnout", "juggling"], freq: FREQ.weekdays, tod: TOD.evening },
    { name: "Take a 1-hour digital detox", emoji: "📵", tags: ["doomscrolling", "overthinking"], freq: FREQ.tueFri, tod: TOD.evening },
  ],
  "cat-fitness": [
    { name: "Stretch for 10 min", emoji: "🤸", tags: ["no-routine", "burnout"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Go for a walk", emoji: "🚶", tags: ["burnout", "overthinking"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Drink more water", emoji: "💧", tags: ["no-routine", "sticking-long-term"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Do 20 squats", emoji: "🏋️", tags: ["procrastination", "no-start"], freq: FREQ.monWedFri, tod: TOD.morning },
    { name: "Take the stairs today", emoji: "🪜", tags: ["no-routine", "juggling"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Do a 5-minute morning stretch", emoji: "🌅", tags: ["no-routine", "no-start"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Dance to one song", emoji: "💃", tags: ["burnout", "overthinking"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Do a quick home workout", emoji: "🏠", tags: ["procrastination", "sticking-long-term"], freq: FREQ.monWedFri, tod: TOD.morning },
    { name: "Go for a jog", emoji: "🏃", tags: ["burnout", "juggling"], freq: FREQ.tueThuSat, tod: TOD.morning },
    { name: "Practice deep breathing", emoji: "🌬️", tags: ["overthinking", "burnout"], freq: FREQ.daily, tod: TOD.evening },
  ],
  "cat-home": [
    { name: "Make my bed", emoji: "🛏️", tags: ["no-routine", "sticking-long-term"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Wash the dishes", emoji: "🍽️", tags: ["procrastination", "losing-track"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Tidy one space", emoji: "🧹", tags: ["no-start", "juggling"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Do one load of laundry", emoji: "🧺", tags: ["procrastination", "losing-track"], freq: FREQ.monThu, tod: TOD.morning },
    { name: "Wipe down the kitchen counter", emoji: "🧽", tags: ["no-routine", "procrastination"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Take out the trash", emoji: "🗑️", tags: ["losing-track", "procrastination"], freq: FREQ.tueFri, tod: TOD.evening },
    { name: "Declutter for 10 minutes", emoji: "📦", tags: ["overthinking", "no-start"], freq: FREQ.wedSat, tod: TOD.afternoon },
    { name: "Vacuum one room", emoji: "🧹", tags: ["juggling", "procrastination"], freq: FREQ.sat, tod: TOD.afternoon },
    { name: "Water my plants", emoji: "🪴", tags: ["losing-track", "no-routine"], freq: FREQ.monThu, tod: TOD.morning },
    { name: "Organize one drawer", emoji: "🗄️", tags: ["no-start", "juggling"], freq: FREQ.sun, tod: TOD.afternoon },
  ],
  "cat-productivity": [
    { name: "Plan tomorrow's top 3", emoji: "📝", tags: ["losing-track", "no-routine"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Clear my inbox", emoji: "📬", tags: ["distraction", "losing-track"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Do a 25-min focus session", emoji: "⏱️", tags: ["distraction", "procrastination"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Write a to-do list", emoji: "✅", tags: ["losing-track", "overthinking"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Time-block my morning", emoji: "🗓️", tags: ["juggling", "no-routine"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Batch my small tasks", emoji: "🧩", tags: ["juggling", "distraction"], freq: FREQ.monWedFri, tod: TOD.afternoon },
    { name: "Do my hardest task first", emoji: "🎯", tags: ["procrastination", "no-start"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Set a timer and single-task", emoji: "⏲️", tags: ["distraction", "overthinking"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Review my week every Sunday", emoji: "🔁", tags: ["losing-track", "no-routine"], freq: FREQ.sun, tod: TOD.evening },
    { name: "Declutter my desktop / files", emoji: "🖥️", tags: ["distraction", "no-start"], freq: FREQ.fri, tod: TOD.afternoon },
  ],
  "cat-finance": [
    { name: "Log today's spending", emoji: "🧾", tags: ["losing-track", "no-routine"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Check my budget", emoji: "💰", tags: ["losing-track", "overthinking"], freq: FREQ.monThu, tod: TOD.evening },
    { name: "Try a no-spend day", emoji: "🪙", tags: ["doomscrolling", "procrastination"], freq: FREQ.wed, tod: TOD.morning },
    { name: "Review one subscription", emoji: "📄", tags: ["losing-track", "procrastination"], freq: FREQ.sat, tod: TOD.evening },
    { name: "Move ₱50 into savings", emoji: "🏦", tags: ["no-start", "sticking-long-term"], freq: FREQ.fri, tod: TOD.morning },
    { name: "Check my bank balance", emoji: "🏧", tags: ["losing-track", "distraction"], freq: FREQ.monThu, tod: TOD.morning },
    { name: "Meal-plan to cut spending", emoji: "🍱", tags: ["no-routine", "juggling"], freq: FREQ.sun, tod: TOD.evening },
    { name: "Cancel one unused subscription", emoji: "✂️", tags: ["procrastination", "no-start"], freq: FREQ.sat, tod: TOD.evening },
    { name: "Track a recurring bill", emoji: "📆", tags: ["losing-track", "juggling"], freq: FREQ.fri, tod: TOD.evening },
    { name: "Set a weekly spending limit", emoji: "📉", tags: ["overthinking", "sticking-long-term"], freq: FREQ.sun, tod: TOD.evening },
  ],
  "cat-work": [
    { name: "Plan my workday", emoji: "🗂️", tags: ["losing-track", "no-routine"] },
    { name: "Take a screen break", emoji: "👀", tags: ["burnout", "distraction"] },
    { name: "Clear one task off my list", emoji: "✅", tags: ["procrastination", "no-start"] },
    { name: "Reply to my top 3 emails", emoji: "📧", tags: ["losing-track", "distraction"] },
    { name: "Tidy my workspace", emoji: "🧹", tags: ["no-routine", "juggling"] },
    { name: "Take a proper lunch break", emoji: "🍱", tags: ["burnout", "juggling"] },
    { name: "Write tomorrow's priorities", emoji: "📝", tags: ["losing-track", "overthinking"] },
    { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙", tags: ["losing-track", "no-routine"] },
    { name: "Silence notifications for an hour", emoji: "🔕", tags: ["distraction", "doomscrolling"] },
    { name: "Ask for help on a stuck task", emoji: "🙋", tags: ["overthinking", "no-start"] },
  ],
  "cat-study": [
    { name: "Read for 15 min", emoji: "📖", tags: ["procrastination", "no-routine"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Review my notes", emoji: "🗒️", tags: ["losing-track", "sticking-long-term"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Learn something new", emoji: "💡", tags: ["no-start", "distraction"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Watch one educational video", emoji: "🎥", tags: ["doomscrolling", "no-start"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Practice a language for 10 min", emoji: "🗣️", tags: ["sticking-long-term", "no-routine"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Do one practice quiz", emoji: "✏️", tags: ["procrastination", "overthinking"], freq: FREQ.tueFri, tod: TOD.afternoon },
    { name: "Summarize what I learned today", emoji: "📓", tags: ["losing-track", "overthinking"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Listen to a podcast", emoji: "🎧", tags: ["juggling", "doomscrolling"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Teach a concept out loud", emoji: "👩‍🏫", tags: ["overthinking", "no-start"], freq: FREQ.wedSat, tod: TOD.evening },
    { name: "Read one article in my field", emoji: "📰", tags: ["distraction", "juggling"], freq: FREQ.daily, tod: TOD.morning },
  ],
  "cat-art": [
    { name: "Work on a project for 15 min", emoji: "🎨", tags: ["procrastination", "no-start"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Sketch or doodle", emoji: "✏️", tags: ["overthinking", "burnout"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Journal a quick idea", emoji: "💭", tags: ["overthinking", "losing-track"], freq: FREQ.daily, tod: TOD.evening },
    { name: "Brainstorm 5 ideas", emoji: "🧠", tags: ["no-start", "overthinking"], freq: FREQ.monThu, tod: TOD.morning },
    { name: "Take a photo of something inspiring", emoji: "📸", tags: ["burnout", "distraction"], freq: FREQ.daily, tod: TOD.afternoon },
    { name: "Write 100 words", emoji: "✍️", tags: ["procrastination", "no-start"], freq: FREQ.daily, tod: TOD.morning },
    { name: "Try a new creative tool or technique", emoji: "🧪", tags: ["no-start", "sticking-long-term"], freq: FREQ.sat, tod: TOD.afternoon },
    { name: "Organize my project files", emoji: "🗃️", tags: ["juggling", "losing-track"], freq: FREQ.sun, tod: TOD.afternoon },
    { name: "Share my work with someone", emoji: "📤", tags: ["procrastination", "overthinking"], freq: FREQ.fri, tod: TOD.afternoon },
    { name: "Set a 20-min 'just play' session", emoji: "🎲", tags: ["burnout", "doomscrolling"], freq: FREQ.wedSat, tod: TOD.evening },
  ],
  // Generic fallback for the School tab — only shown if a level answer is
  // somehow missing (elementary/high-school get their own tagged lists
  // below; college defers entirely to the specific-course content in
  // onboardingFieldHabits.js).
  "cat-school": [
    { name: "Review today's lesson", emoji: "📖", tags: ["no-routine", "sticking-long-term"] },
    { name: "Finish my homework", emoji: "📚", tags: ["losing-track", "procrastination"] },
    { name: "Plan my study schedule for the week", emoji: "🗓️", tags: ["no-routine", "juggling"] },
  ],
};

// Elementary and High School get their own tagged lists (same 10 obstacle
// tags as STARTER_HABITS above) — kept separate from STARTER_HABITS["cat-school"]
// since the two grade bands call for different framing (younger/concrete vs.
// older/more independent), and from each other by educationLevel at render
// time (see OnboardingSection2.jsx's fieldHabitsFor).
export const STARTER_HABITS_ELEMENTARY = [
  { name: "Finish my homework", emoji: "📚", tags: ["losing-track", "procrastination"] },
  { name: "Review today's lesson", emoji: "📖", tags: ["no-routine", "sticking-long-term"] },
  { name: "Read for 15 minutes", emoji: "📗", tags: ["procrastination", "no-routine"] },
  { name: "Pack my bag for tomorrow", emoji: "🎒", tags: ["losing-track", "no-routine"] },
  { name: "Practice my spelling words", emoji: "✏️", tags: ["sticking-long-term", "procrastination"] },
  { name: "Do a fun learning game", emoji: "🧩", tags: ["distraction", "no-start"] },
  { name: "Tidy my study desk", emoji: "🗂️", tags: ["juggling", "no-start"] },
  { name: "Ask for help when I'm stuck", emoji: "🙋", tags: ["overthinking", "no-start"] },
  { name: "Take a break from the screen", emoji: "📵", tags: ["burnout", "doomscrolling"] },
  { name: "Write in my journal", emoji: "📔", tags: ["overthinking", "losing-track"] },
];

export const STARTER_HABITS_HIGH_SCHOOL = [
  { name: "Review today's notes", emoji: "🗒️", tags: ["losing-track", "no-routine"] },
  { name: "Study for my next quiz/exam", emoji: "📚", tags: ["procrastination", "sticking-long-term"] },
  { name: "Finish my assignments before the deadline", emoji: "⏰", tags: ["losing-track", "procrastination"] },
  { name: "Do a 25-minute focus session", emoji: "⏱️", tags: ["distraction", "procrastination"] },
  { name: "Plan my study schedule for the week", emoji: "🗓️", tags: ["no-routine", "juggling"] },
  { name: "Read one chapter ahead", emoji: "📖", tags: ["no-start", "sticking-long-term"] },
  { name: "Organize my school files/notes", emoji: "🗃️", tags: ["juggling", "losing-track"] },
  { name: "Take a break from social media while studying", emoji: "📵", tags: ["doomscrolling", "distraction"] },
  { name: "Review my grades/progress", emoji: "📊", tags: ["overthinking", "losing-track"] },
  { name: "Ask a teacher or classmate for help", emoji: "🙋", tags: ["overthinking", "no-start"] },
];
